import React, { useState, useEffect, useCallback } from 'react';
import { ogService, customerService } from '../services/api';
import { useAuthStore } from '../store';
import { GlassPanel, GlassButton, GlassInput, GlassCard, GlassSelect } from '../components/GlassUI';
import {
  FaGem, FaSearch, FaFlask, FaCalculator, FaExchangeAlt, FaCheckCircle,
  FaRupeeSign, FaArrowLeft, FaArrowRight, FaUser, FaWeight,
  FaClipboardCheck, FaInfoCircle,
  FaPlus, FaTrash, FaTimes, FaChartBar, FaCog,
  FaFilter, FaEye, FaCertificate, FaEdit, FaDownload,
} from 'react-icons/fa';

interface OGPageProps {
  onNavigate?: (page: string, id?: string) => void;
}

type ViewMode = 'list' | 'create' | 'detail' | 'reports' | 'settings';

interface ItemForm {
  itemName: string; itemType: string; quantity: number;
  grossWeight: string; netWeight: string;
  hallmark: boolean; hallmarkNumber: string;
  estimatedPurity: string; remarks: string; photos: string[];
}

interface TestForm {
  testingMethod: string; testedBy: string; machineNumber: string;
  resultPurity: string; karatValue: string;
}

interface DeductionForm {
  deductionType: string; reason: string; amount: string;
}

const ITEM_TYPES = ['Ring', 'Chain', 'Necklace', 'Bangles', 'Earrings', 'Coin', 'Bracelet', 'Pendant', 'Other'];
const TEST_METHODS = ['Karat Meter', 'XRF Machine', 'Acid Test', 'Touchstone Test'];
const KARAT_VALUES = ['24K', '22K', '21K', '20K', '18K', '14K', 'Custom'];
const DEDUCTION_TYPES = ['Melting Loss', 'Impurity', 'Refining', 'Stone Removal', 'Handling', 'Broken Item', 'Other'];
const TXN_TYPES = [
  { value: 'BUYBACK', label: 'Buyback Only', desc: 'Customer sells old gold for cash', icon: '💰' },
  { value: 'EXCHANGE', label: 'Exchange', desc: 'Customer exchanges old gold for new jewellery', icon: '🔄' },
  { value: 'BUYBACK_PURCHASE', label: 'Buyback + Purchase', desc: 'Sell old gold and buy new jewellery separately', icon: '💎' },
];
const STATUS_FLOW = ['DRAFT', 'TESTING', 'VALUATION', 'PENDING_APPROVAL', 'APPROVED', 'COMPLETED'];

const createEmptyItem = (): ItemForm => ({
  itemName: '', itemType: 'Ring', quantity: 1,
  grossWeight: '', netWeight: '', hallmark: false,
  hallmarkNumber: '', estimatedPurity: '22K', remarks: '', photos: [],
});

const createEmptyTest = (): TestForm => ({
  testingMethod: 'XRF Machine', testedBy: '', machineNumber: '',
  resultPurity: '', karatValue: '22K',
});

const createEmptyDeduction = (): DeductionForm => ({
  deductionType: 'Refining', reason: '', amount: '',
});

const fmt = (v: number) => v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtW = (v: number) => v.toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

const OLDGOLD_STATUS: Record<string, { bg: string; text: string; label: string }> = {
  DRAFT: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
  TESTING: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Testing' },
  VALUATION: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Valuation' },
  PENDING_APPROVAL: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Pending Approval' },
  APPROVED: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' },
  COMPLETED: { bg: 'bg-green-500', text: 'text-white', label: 'Completed' },
  CANCELLED: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' },
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const c = OLDGOLD_STATUS[status] || { bg: 'bg-slate-100', text: 'text-slate-600', label: status };
  return <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${c.bg} ${c.text}`}>{c.label}</span>;
};

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

const OldGoldPage: React.FC<OGPageProps> = ({ onNavigate }) => {
  useAuthStore(); // side-effect: subscribe to auth state
  // Always land on the main list when opening Old Gold. Previously the last
  // sub-view (New Transaction / Reports / Detail) was restored, which was
  // confusing — the menu item should reliably show the transaction list.
  const [view, setView] = useState<ViewMode>('list');
  const [wizardStep, setWizardStep] = useState(0);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);

  const persistView = (v: ViewMode, s?: number) => {
    // No longer persist across visits; kept as a small no-op helper so the rest
    // of the component keeps working unchanged.
    void v; void s;
  };

  useEffect(() => {
    try {
      localStorage.removeItem('og_view');
      localStorage.removeItem('og_step');
      localStorage.removeItem('og_detail');
    } catch {}
  }, []);

  const handleHeaderBack = () => {
    if (view === 'create' && wizardStep > 0) {
      const newStep = wizardStep - 1;
      setWizardStep(newStep);
      persistView('create', newStep);
    } else {
      setView('list');
      persistView('list');
    }
  };

  const handleCreateNew = () => {
    setEditId(null);
    setWizardStep(0);
    setView('create');
    persistView('create', 0);
  };

  const handleEdit = (id: string) => {
    setEditId(id);
    setWizardStep(0);
    setView('create');
    persistView('create', 0);
  };

  return (
    <div className='min-h-screen bg-slate-50 p-4 md:p-8 text-slate-900'>
      <div className='max-w-6xl mx-auto'>
        <div className='flex items-center justify-between mb-6'>
          <div className='flex items-center gap-3'>
            {view !== 'list' && (
              <GlassButton variant='secondary' onClick={handleHeaderBack} className='!px-3 !py-2'>
                <FaArrowLeft />
              </GlassButton>
            )}
            <div>
              <h1 className='text-3xl font-bold text-slate-900 flex items-center gap-3'>
                <FaGem className='text-amber-500' /> Old Gold Management
              </h1>
              <p className='text-slate-500 mt-1'>Buyback and exchange old gold with full transparency</p>
            </div>
          </div>
          <div className='flex flex-wrap gap-2'>
            {view !== 'reports' && (
              <GlassButton variant='secondary' onClick={() => { setView('reports'); persistView('reports'); }} title='History & totals of past old-gold transactions'>
                <FaChartBar className='inline mr-1' /> Reports
              </GlassButton>
            )}
            {view !== 'settings' && view !== 'create' && (
              <GlassButton variant='secondary' onClick={() => { setView('settings'); persistView('settings'); }} title='Default gold rates and other Old Gold preferences'>
                <FaCog className='inline mr-1' /> Settings
              </GlassButton>
            )}
            {view === 'list' && (
              <GlassButton variant='primary' onClick={handleCreateNew} title='Start a new buyback or exchange'>
                <FaPlus className='inline mr-1' /> New Transaction
              </GlassButton>
            )}
          </div>
        </div>

        {view === 'list' && (
          <>
            {/* Short explanation of the three actions, per request. */}
            <GlassPanel className='p-4 mb-4'>
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm'>
                <div className='flex items-start gap-2'>
                  <FaPlus className='text-amber-500 mt-0.5' />
                  <div><span className='font-semibold text-slate-800'>New Transaction</span>
                    <p className='text-slate-500 text-xs'>Record a customer's old gold — buyback for cash, exchange for new jewellery, or both.</p>
                  </div>
                </div>
                <div className='flex items-start gap-2'>
                  <FaChartBar className='text-blue-500 mt-0.5' />
                  <div><span className='font-semibold text-slate-800'>Reports</span>
                    <p className='text-slate-500 text-xs'>See totals and history of past buybacks and exchanges.</p>
                  </div>
                </div>
                <div className='flex items-start gap-2'>
                  <FaCog className='text-slate-500 mt-0.5' />
                  <div><span className='font-semibold text-slate-800'>Settings</span>
                    <p className='text-slate-500 text-xs'>Set the default gold rate, buyback discount and deductions used for pricing.</p>
                  </div>
                </div>
              </div>
            </GlassPanel>
            <ListView
              onNavigate={onNavigate}
              onCreateNew={handleCreateNew}
              onEdit={handleEdit}
              onViewDetail={(id) => { setDetailId(id); setView('detail'); persistView('detail'); }}
            />
          </>
        )}
        {view === 'create' && <CreateWizard editId={editId} wizardStep={wizardStep} setWizardStep={(s) => { setWizardStep(s); persistView('create', s); }} onBack={() => { setView('list'); persistView('list'); }} onCreated={() => { setView('list'); persistView('list'); }} />}
        {view === 'detail' && <DetailView transactionId={detailId || undefined} onBack={() => { setView('list'); persistView('list'); }} onEdit={handleEdit} />}
        {view === 'reports' && <ReportsView />}
        {view === 'settings' && <SettingsView />}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// LIST VIEW
// ═══════════════════════════════════════════════════════════

const ListView: React.FC<{ onNavigate?: any; onCreateNew: () => void; onViewDetail: (id: string) => void; onEdit: (id: string) => void }> = ({ onNavigate, onCreateNew, onViewDetail, onEdit }) => {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const limit = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ogService.getTransactions({ page, limit, search, transactionType: filterType, status: filterStatus });
      setTransactions(res.data.transactions || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, search, filterType, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await ogService.deleteTransaction(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to delete transaction');
    } finally { setDeleting(false); }
  };

  const canEdit = (status: string) => ['DRAFT', 'TESTING', 'VALUATION', 'PENDING_APPROVAL', 'APPROVED'].includes(status);

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <GlassPanel className='p-4 md:p-6 mb-4'>
        <div className='flex flex-wrap gap-3 items-center'>
          <div className='flex-1 min-w-[200px]'>
            <div className='relative'>
              <FaSearch className='absolute left-3 top-1/2 -translate-y-1/2 text-slate-400' />
              <input
                className='w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400'
                placeholder='Search by transaction # or customer name...'
                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
          </div>
          <GlassSelect value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }} className='w-auto min-w-[140px]'>
            <option value=''>All Types</option>
            <option value='BUYBACK'>Buyback</option>
            <option value='EXCHANGE'>Exchange</option>
            <option value='BUYBACK_PURCHASE'>Buyback + Purchase</option>
          </GlassSelect>
          <GlassSelect value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} className='w-auto min-w-[140px]'>
            <option value=''>All Status</option>
            {STATUS_FLOW.map(s => <option key={s} value={s}>{OLDGOLD_STATUS[s]?.label || s}</option>)}
            <option value='CANCELLED'>Cancelled</option>
          </GlassSelect>
          <GlassButton variant='primary' onClick={onCreateNew}>
            <FaPlus className='inline mr-1' /> New
          </GlassButton>
        </div>
      </GlassPanel>

      {loading ? (
        <GlassPanel className='p-12 text-center'><p className='text-slate-500'>Loading transactions...</p></GlassPanel>
      ) : transactions.length === 0 ? (
        <GlassPanel className='p-12 text-center'>
          <FaGem className='text-slate-300 text-4xl mx-auto mb-3' />
          <p className='text-slate-500 mb-4'>No old gold transactions found</p>
          <GlassButton variant='primary' onClick={onCreateNew}><FaPlus className='inline mr-1' /> Create First Transaction</GlassButton>
        </GlassPanel>
      ) : (
        <div className='space-y-3'>
          {transactions.map((t: any) => (
            <GlassPanel key={t.id} className='p-4 hover:shadow-lg transition-all'>
              <div className='flex items-center justify-between gap-4'>
                <div className='min-w-0 flex-1 cursor-pointer' onClick={() => onViewDetail(t.id)}>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <span className='font-mono text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded'>{t.transactionNumber}</span>
                    <StatusPill status={t.status} />
                    <span className='text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium'>
                      {t.transactionType === 'BUYBACK_PURCHASE' ? 'Buyback + Purchase' : t.transactionType}
                    </span>
                  </div>
                  <p className='text-sm font-semibold text-slate-900 mt-1'>{t.customer?.customerName || 'Unknown'}</p>
                  <p className='text-xs text-slate-500'>
                    {t.totalNetWeight?.toFixed(3)}g net · {t.items?.length || 0} item(s) · {t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-IN') : ''}
                  </p>
                </div>
                <div className='flex items-center gap-3 flex-shrink-0'>
                  <div className='text-right'>
                    <p className='font-bold text-amber-700 text-lg flex items-center gap-1 justify-end'>
                      <FaRupeeSign className='text-amber-500' size={14} />{fmt(t.finalAmount || 0)}
                    </p>
                    <p className='text-xs text-slate-500'>Fine Gold: {t.totalFineGold?.toFixed(3)}g</p>
                    <p className='text-xs text-slate-500'>
                      Paid: ₹{fmt(t.amountPaid || 0)} · Balance: ₹{fmt(t.balanceAmount || 0)}
                    </p>
                    {t.transactionType !== 'BUYBACK' && (
                      <p className={`text-xs font-semibold ${(t.exchangeBalance || 0) >= 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        {(t.exchangeBalance || 0) >= 0 ? 'Balance Payable' : 'Refund Due'}: ₹{fmt(Math.abs(t.exchangeBalance || 0))}
                      </p>
                    )}
                  </div>
                  <div className='flex flex-col gap-1'>
                    <button
                      onClick={(e) => { e.stopPropagation(); onViewDetail(t.id); }}
                      title='View'
                      className='text-slate-500 hover:text-slate-800 p-1.5'
                    >
                      <FaEye size={14} />
                    </button>
                    {canEdit(t.status) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onEdit(t.id); }}
                        title='Edit'
                        className='text-blue-500 hover:text-blue-700 p-1.5'
                      >
                        <FaEdit size={14} />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(t); }}
                      title='Delete'
                      className='text-red-500 hover:text-red-700 p-1.5'
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </GlassPanel>
          ))}
          {totalPages > 1 && (
            <div className='flex justify-center gap-2 mt-4'>
              <GlassButton variant='secondary' disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</GlassButton>
              <GlassCard className='px-4 py-2 text-sm text-slate-600'>{page} / {totalPages}</GlassCard>
              <GlassButton variant='secondary' disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</GlassButton>
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4' onClick={() => !deleting && setDeleteTarget(null)}>
          <GlassPanel className='max-w-sm w-full p-6 text-center' onClick={(e) => e.stopPropagation()}>
            <h2 className='text-xl font-bold text-slate-900 mb-2'>Delete Transaction?</h2>
            <p className='text-slate-600 mb-1 text-sm'>
              {deleteTarget.transactionNumber} for {deleteTarget.customer?.customerName || 'customer'} will be permanently removed.
            </p>
            <p className='text-slate-400 mb-6 text-xs'>This cannot be undone.</p>
            <div className='flex gap-3'>
              <GlassButton variant='secondary' className='flex-1' disabled={deleting} onClick={() => setDeleteTarget(null)}>Cancel</GlassButton>
              <GlassButton
                variant='primary'
                className='flex-1 !bg-red-600 hover:!bg-red-700'
                disabled={deleting}
                onClick={handleDelete}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </GlassButton>
            </div>
          </GlassPanel>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// CREATE WIZARD
// ═══════════════════════════════════════════════════════════

const WIZARD_STEPS = [
  { label: 'Customer', icon: FaUser },
  { label: 'Type', icon: FaExchangeAlt },
  { label: 'Items', icon: FaGem },
  { label: 'Testing', icon: FaFlask },
  { label: 'Valuation', icon: FaCalculator },
  { label: 'Deductions', icon: FaClipboardCheck },
  { label: 'Review', icon: FaEye },
];

const CreateWizard: React.FC<{ editId?: string | null; wizardStep: number; setWizardStep: (s: number) => void; onBack: () => void; onCreated: () => void }> = ({ editId, wizardStep, setWizardStep, onBack, onCreated }) => {
  useAuthStore(); // subscribe to auth state
  const step = wizardStep;
  const setStep = setWizardStep;
  const isEdit = !!editId;
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [customer, setCustomer] = useState<any>(null);
  const [customerList, setCustomerList] = useState<any[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [phone, setPhone] = useState('');
  const [custName, setCustName] = useState('');
  const [custAddress, setCustAddress] = useState('');

  const [txnType, setTxnType] = useState('BUYBACK');
  const [items, setItems] = useState<ItemForm[]>([createEmptyItem()]);
  const [tests, setTests] = useState<TestForm[]>([createEmptyTest()]);
  const [deductions, setDeductions] = useState<DeductionForm[]>([]);

  const [goldRate, setGoldRate] = useState<any>(null);
  const [manualRate24K, setManualRate24K] = useState('');
  const [manualBuybackDiscount, setManualBuybackDiscount] = useState('');
  const [discountType, setDiscountType] = useState<'flat' | 'percent'>('flat');
  const [useManualRate, setUseManualRate] = useState(false);
  const [linkedOrderId, setLinkedOrderId] = useState('');
  const [exchangeSubtotal, setExchangeSubtotal] = useState('');
  const [notes, setNotes] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  void useManualRate; // used implicitly via setUseManualRate in onChange handlers

  useEffect(() => {
    const loadCustomers = async () => {
      setLoading(true);
      try {
        const res = await customerService.getAll(1, 500, '');
        setCustomerList(res.data.customers || res.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    loadCustomers();
  }, []);

  useEffect(() => {
    const loadRate = async () => {
      try {
        const res = await ogService.getGoldRate();
        const rate = res.data.rate || res.data;
        setGoldRate(rate);
        setManualRate24K(String(rate.rate24K || ''));
        setManualBuybackDiscount(String(rate.buybackDiscount || '200'));
      } catch (e) { console.error(e); }
    };
    loadRate();
  }, []);

  // Edit mode: pull the existing transaction and fill every step.
  useEffect(() => {
    if (!editId) return;
    const idToLoad: string = editId;
    const loadExisting = async () => {
      setLoading(true);
      try {
        const res = await ogService.getTransactionById(idToLoad);
        const t = res.data.transaction || res.data;
        if (!t) return;
        if (t.customer) setCustomer(t.customer);
        setTxnType(t.transactionType || 'BUYBACK');
        if (Array.isArray(t.items) && t.items.length) {
          setItems(t.items.map((it: any) => ({
            itemName: it.itemName || '', itemType: it.itemType || 'Ring', quantity: it.quantity || 1,
            grossWeight: String(it.grossWeight ?? ''), netWeight: String(it.netWeight ?? ''),
            hallmark: !!it.hallmark, hallmarkNumber: it.hallmarkNumber || '',
            estimatedPurity: it.estimatedPurity || '22K', remarks: it.remarks || '', photos: [],
          })));
        }
        if (Array.isArray(t.tests) && t.tests.length) {
          setTests(t.tests.map((tt: any) => ({
            testingMethod: tt.testingMethod || 'XRF Machine', testedBy: tt.testedBy || '',
            machineNumber: tt.machineNumber || '', resultPurity: String(tt.resultPurity ?? ''),
            karatValue: tt.karatValue || '22K',
          })));
        }
        if (Array.isArray(t.deductions)) {
          setDeductions(t.deductions.map((d: any) => ({
            deductionType: d.deductionType || 'Refining', reason: d.reason || '', amount: String(d.amount ?? ''),
          })));
        }
        if (t.goldRate24K) setManualRate24K(String(t.goldRate24K));
        if (t.exchangeSubtotal) setExchangeSubtotal(String(t.exchangeSubtotal));
        setLinkedOrderId(t.linkedOrderId || '');
        setNotes(t.notes || '');
      } catch (e: any) {
        alert(e.response?.data?.error || 'Failed to load transaction for editing');
      } finally { setLoading(false); }
    };
    loadExisting();
  }, [editId]);

  const selectCustomer = (c: any) => {
    setCustomer(c);
    setShowNewForm(false);
  };

  const createAndSelectCustomer = async () => {
    if (!phone || !custName) return;
    setLoading(true);
    try {
      const res = await customerService.createOrUpdate({
        cellNumber: phone, customerName: custName,
        location: custAddress || undefined,
      });
      const newCust = res.data.customer;
      setCustomer(newCust);
      setCustomerList(prev => [newCust, ...prev]);
      setShowNewForm(false);
    } catch (e: any) { alert(e.response?.data?.error || 'Failed to create customer'); }
    finally { setLoading(false); }
  };

  const filteredCustomers = customerList.filter((c: any) => {
    if (!customerSearch) return true;
    const q = customerSearch.toLowerCase();
    return (
      (c.customerName || '').toLowerCase().includes(q) ||
      (c.cellNumber || '').includes(q) ||
      (c.location || '').toLowerCase().includes(q)
    );
  });

  const updateItem = (idx: number, field: string, val: any) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));
  };
  const addItem = () => setItems(prev => [...prev, createEmptyItem()]);
  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));

  const updateTest = (idx: number, field: string, val: any) => {
    setTests(prev => prev.map((t, i) => i === idx ? { ...t, [field]: val } : t));
  };
  const addTest = () => setTests(prev => [...prev, createEmptyTest()]);
  const removeTest = (idx: number) => setTests(prev => prev.filter((_, i) => i !== idx));

  const updateDeduction = (idx: number, field: string, val: any) => {
    setDeductions(prev => prev.map((d, i) => i === idx ? { ...d, [field]: val } : d));
  };
  const addDeduction = () => setDeductions(prev => [...prev, createEmptyDeduction()]);
  const removeDeduction = (idx: number) => setDeductions(prev => prev.filter((_, i) => i !== idx));

  // Calculations
  const calcTotals = () => {
    let totalGross = 0, totalStone = 0, totalNet = 0;
    items.forEach(it => {
      const g = parseFloat(it.grossWeight) || 0;
      const n = parseFloat(it.netWeight) || 0;
      totalGross += g;
      totalStone += Math.max(0, g - n);
      totalNet += n;
    });

    let avgPurity = 0;
    if (tests.length > 0 && tests.some(t => parseFloat(t.resultPurity) > 0)) {
      const validTests = tests.filter(t => parseFloat(t.resultPurity) > 0);
      avgPurity = validTests.reduce((s, t) => s + parseFloat(t.resultPurity), 0) / validTests.length;
    } else {
      const purities: Record<string, number> = { '24K': 99.9, '22K': 91.6, '21K': 87.5, '20K': 83.3, '18K': 75, '14K': 58.5 };
      const validItems = items.filter(it => it.estimatedPurity && purities[it.estimatedPurity]);
      if (validItems.length > 0) {
        avgPurity = validItems.reduce((s, it) => s + (purities[it.estimatedPurity] || 0), 0) / validItems.length;
      }
    }

    const fineGold = totalNet * (avgPurity / 100);
    const rate24K = parseFloat(manualRate24K) || goldRate?.rate24K || 9150;
    const rawStoreCharges = manualBuybackDiscount === '' ? 0 : (parseFloat(manualBuybackDiscount) || 0);
    const storeCharges = discountType === 'percent' ? (rate24K * rawStoreCharges / 100) : rawStoreCharges;
    const marketRate = rate24K;
    const goldValue = fineGold * marketRate;
    const totalDeductions = deductions.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
    const finalAmount = Math.max(0, goldValue - storeCharges - totalDeductions);
    const subTotal = parseFloat(exchangeSubtotal) || 0;
    const exchangeBalance = txnType !== 'BUYBACK' ? subTotal - finalAmount : 0;

    return {
      totalGross, totalStone, totalNet, avgPurity, fineGold,
      rate24K, marketRate, storeCharges,
      goldValue, totalDeductions, finalAmount, exchangeBalance,
    };
  };

  const totals = calcTotals();
  const rawStoreCharges = manualBuybackDiscount === '' ? 0 : (parseFloat(manualBuybackDiscount) || 0);

  const canProceed = () => {
    switch (step) {
      case 0: return customer !== null;
      case 1: return txnType;
      case 2: return items.length > 0 && items.every(it => it.itemName && parseFloat(it.grossWeight) > 0 && parseFloat(it.netWeight) > 0);
      case 3: return tests.length > 0 && tests.every(t => t.testedBy && parseFloat(t.resultPurity) > 0 && parseFloat(t.resultPurity) <= 100);
      case 4: return totals.finalAmount >= 0;
      case 5: return true;
      case 6: return true;
      default: return true;
    }
  };

  const handleSubmit = async () => {
    if (!customer && !custName) { alert('Customer is required'); return; }
    setSubmitting(true);
    try {
      let customerId = customer?.id;
      if (!customerId) {
        const res = await customerService.createOrUpdate({ cellNumber: phone, customerName: custName });
        customerId = res.data.customer?.id;
      }

      const payload = {
        transactionType: txnType,
        customerId,
        items: items.map(it => ({
          itemName: it.itemName, itemType: it.itemType, quantity: it.quantity,
          grossWeight: parseFloat(it.grossWeight) || 0,
          netWeight: parseFloat(it.netWeight) || 0,
          stoneWeight: Math.max(0, (parseFloat(it.grossWeight) || 0) - (parseFloat(it.netWeight) || 0)),
          hallmark: it.hallmark, hallmarkNumber: it.hallmarkNumber || undefined,
          estimatedPurity: it.estimatedPurity, remarks: it.remarks || undefined,
        })),
        tests: tests.map(t => ({
          testingMethod: t.testingMethod, testedBy: t.testedBy,
          machineNumber: t.machineNumber || undefined,
          resultPurity: parseFloat(t.resultPurity) || 0,
          karatValue: t.karatValue || undefined,
        })),
        deductions: deductions.filter(d => d.reason && parseFloat(d.amount) > 0).map(d => ({
          deductionType: d.deductionType, reason: d.reason,
          amount: parseFloat(d.amount) || 0,
        })),
        goldRate24K: totals.rate24K,
        goldRate22K: Math.round(totals.rate24K * 0.9167),
        marketRate: totals.marketRate,
        buybackDiscount: totals.storeCharges,
        discountType,
        linkedOrderId: linkedOrderId || undefined,
        exchangeSubtotal: txnType !== 'BUYBACK' ? (parseFloat(exchangeSubtotal) || 0) : undefined,
        notes: notes || undefined,
      };

      if (isEdit && editId) {
        await ogService.updateTransaction(editId, payload);
      } else {
        await ogService.createTransaction(payload);
      }
      onCreated();
    } catch (e: any) {
      alert(e.response?.data?.error || (isEdit ? 'Failed to update transaction' : 'Failed to create transaction'));
    } finally { setSubmitting(false); }
  };

  const StepIndicator = () => (
    <div className='mb-6 overflow-x-auto'>
      <div className='flex items-center justify-start md:justify-center gap-1 min-w-max px-2'>
        {WIZARD_STEPS.map((s, i) => (
          <React.Fragment key={i}>
            <div className='flex flex-col items-center'>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                step === i ? 'bg-amber-500 text-white scale-110 shadow-lg shadow-amber-200'
                  : step > i ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {step > i ? <FaCheckCircle /> : <s.icon />}
              </div>
              <span className={`text-[10px] mt-1 font-medium text-center leading-tight max-w-[60px] ${
                step === i ? 'text-amber-700' : step > i ? 'text-green-600' : 'text-slate-400'
              }`}>{s.label}</span>
            </div>
            {i < WIZARD_STEPS.length - 1 && (
              <div className={`w-6 md:w-8 h-0.5 mt-[-14px] ${step > i ? 'bg-green-500' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const LiveBanner = () => {
    if (totals.finalAmount <= 0) return null;
    return (
      <div className='bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl p-4 mb-6 text-white shadow-lg flex items-center justify-between flex-wrap gap-2'>
        <div>
          <p className='text-amber-100 text-xs font-medium uppercase tracking-wide'>Customer Gets</p>
          <p className='text-2xl font-bold flex items-center gap-1'><FaRupeeSign />{fmt(totals.finalAmount)}</p>
        </div>
        <div className='text-right text-xs text-amber-100'>
          <p>{fmtW(totals.totalNet)}g net · {totals.avgPurity.toFixed(1)}% pure</p>
          <p>Fine Gold: {fmtW(totals.fineGold)}g × ₹{fmt(totals.marketRate)}/g</p>
        </div>
      </div>
    );
  };

  const nav = (dir: number) => setStep(Math.max(0, Math.min(WIZARD_STEPS.length - 1, step + dir)));

  const StepHint: React.FC<{ steps: string[] }> = ({ steps }) => (
    <GlassCard className='mb-4 bg-slate-50 border-slate-200'>
      <p className='text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1'><FaInfoCircle className='text-blue-500' /> How to use this step</p>
      <ol className='space-y-1 text-sm text-slate-700 list-decimal list-inside'>
        {steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
    </GlassCard>
  );

  return (
    <div>
      <StepIndicator />
      <LiveBanner />

      {/* STEP 0: Customer */}
      {step === 0 && (
        <GlassPanel className='p-6 md:p-8'>
          <h2 className='text-2xl font-bold mb-1 flex items-center gap-2'><FaUser className='text-blue-500' /> Select Customer</h2>
          <p className='text-slate-500 text-sm mb-5'>Type customer name or phone to search, then tap to select</p>

          <StepHint steps={[
            'Search by typing the customer\'s name or mobile number.',
            'Tap the correct customer from the list to select them.',
            'If the customer is new, tap "Add New Customer" and fill in their name and mobile number, then Create & Select.',
            'Tap "Next: Transaction Type" to continue.',
          ]} />

          {/* Selected customer chip */}
          {customer && (
            <GlassCard className='mb-4 bg-green-50 border-green-200'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-3'>
                  <div className='w-10 h-10 rounded-full bg-green-200 flex items-center justify-center text-green-700 font-bold text-lg'>
                    {(customer.customerName || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <p className='text-green-800 font-bold'>{customer.customerName}</p>
                    <p className='text-green-600 text-sm'>{customer.cellNumber} {customer.location ? `· ${customer.location}` : ''}</p>
                  </div>
                </div>
                <div className='flex items-center gap-2'>
                  <FaCheckCircle className='text-green-500 text-xl' />
                  <button onClick={() => { setCustomer(null); setCustomerSearch(''); }} className='text-slate-400 hover:text-red-500 text-sm font-medium'>Change</button>
                </div>
              </div>
            </GlassCard>
          )}

          {/* Search dropdown */}
          {!customer && (
            <div className='relative mb-4'>
              <div className='relative'>
                <FaSearch className='absolute left-3 top-1/2 -translate-y-1/2 text-slate-400' />
                <input
                  className='w-full pl-10 pr-10 py-3 bg-white border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all'
                  placeholder='Type name or phone number...'
                  value={customerSearch}
                  onChange={e => { setCustomerSearch(e.target.value); setShowDropdown(true); }}
                  onFocus={() => setShowDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 300)}
                  autoFocus
                />
                {customerSearch && (
                  <button onClick={() => { setCustomerSearch(''); setShowDropdown(false); }}
                    className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600'>
                    <FaTimes />
                  </button>
                )}
              </div>

              {/* Dropdown results */}
              {showDropdown && customerSearch && (
                <div className='absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-[280px] overflow-y-auto' onMouseDown={e => e.preventDefault()}>
                  {loading ? (
                    <div className='px-4 py-4 text-center text-slate-400 text-sm'>Loading...</div>
                  ) : filteredCustomers.length === 0 ? (
                    <div className='px-4 py-4 text-center'>
                      <p className='text-slate-500 text-sm'>No customers found</p>
                      <button onMouseDown={e => e.preventDefault()} onClick={() => { setShowDropdown(false); setShowNewForm(true); }}
                        className='mt-2 text-amber-600 text-sm font-semibold hover:underline'>
                        + Add new customer
                      </button>
                    </div>
                  ) : (
                    filteredCustomers.slice(0, 20).map((c: any) => (
                      <button
                        key={c.id}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { selectCustomer(c); setCustomerSearch(''); setShowDropdown(false); }}
                        className='w-full flex items-center gap-3 px-4 py-3 hover:bg-amber-50 transition-all text-left border-b border-slate-50 last:border-0'
                      >
                        <div className='w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold text-xs flex-shrink-0'>
                          {(c.customerName || '?')[0].toUpperCase()}
                        </div>
                        <div className='min-w-0 flex-1'>
                          <p className='font-semibold text-slate-900 truncate text-sm'>{c.customerName}</p>
                          <p className='text-xs text-slate-500 truncate'>{c.cellNumber} {c.location ? `· ${c.location}` : ''}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Add new customer */}
          {!customer && (
            !showNewForm ? (
              <button onClick={() => setShowNewForm(true)} className='w-full py-3 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 hover:border-amber-400 hover:text-amber-600 transition-all text-sm font-medium flex items-center justify-center gap-2'>
                <FaPlus /> Add New Customer
              </button>
            ) : (
              <GlassCard className='bg-amber-50 border-amber-200'>
                <div className='flex items-center justify-between mb-3'>
                  <p className='font-bold text-amber-900 text-sm'>New Customer</p>
                  <button onClick={() => { setShowNewForm(false); setPhone(''); setCustName(''); setCustAddress(''); }} className='text-slate-400 hover:text-red-500'><FaTimes /></button>
                </div>
                <div className='space-y-3'>
                  <div>
                    <label className='block text-slate-700 text-xs font-semibold mb-1'>Full Name *</label>
                    <GlassInput value={custName} onChange={e => setCustName(e.target.value)} placeholder='Customer full name' />
                  </div>
                  <div>
                    <label className='block text-slate-700 text-xs font-semibold mb-1'>Mobile Number *</label>
                    <GlassInput type='tel' value={phone} onChange={e => setPhone(e.target.value)} placeholder='+91...' />
                  </div>
                  <div>
                    <label className='block text-slate-700 text-xs font-semibold mb-1'>Address</label>
                    <GlassInput value={custAddress} onChange={e => setCustAddress(e.target.value)} placeholder='Optional' />
                  </div>
                  <GlassButton variant='primary' className='w-full' disabled={!phone || !custName || loading} onClick={createAndSelectCustomer}>
                    {loading ? 'Creating...' : 'Create & Select'}
                  </GlassButton>
                </div>
              </GlassCard>
            )
          )}

          <div className='mt-6'>
            <GlassButton variant='primary' className='w-full' disabled={!canProceed()} onClick={() => nav(1)}>
              Next: Transaction Type <FaArrowRight className='inline ml-1' />
            </GlassButton>
          </div>
        </GlassPanel>
      )}

      {/* STEP 1: Transaction Type */}
      {step === 1 && (
        <GlassPanel className='p-6 md:p-8'>
          <h2 className='text-2xl font-bold mb-1 flex items-center gap-2'><FaExchangeAlt className='text-green-500' /> Transaction Type</h2>
          <p className='text-slate-500 text-sm mb-6'>Select the type of old gold transaction</p>

          <StepHint steps={[
            'Buyback Only: customer sells old gold for cash only.',
            'Exchange: customer swaps old gold for new jewellery.',
            'Buyback + Purchase: customer sells old gold AND buys new jewellery separately.',
            'For Exchange, enter the total cost of the new jewellery the customer wants.',
          ]} />

          <div className='space-y-3 mb-6'>{TXN_TYPES.map(tx => (
              <label key={tx.value} className={`block p-4 rounded-xl border-2 cursor-pointer transition-all ${
                txnType === tx.value ? 'border-amber-500 bg-amber-50 shadow-sm' : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}>
                <div className='flex items-center gap-3'>
                  <input type='radio' name='txnType' value={tx.value} checked={txnType === tx.value}
                    onChange={() => setTxnType(tx.value)} className='w-5 h-5 accent-amber-500' />
                  <div>
                    <p className='font-bold text-slate-900'>{tx.icon} {tx.label}</p>
                    <p className='text-sm text-slate-600'>{tx.desc}</p>
                  </div>
                </div>
              </label>
            ))}
          </div>

          {(txnType === 'EXCHANGE' || txnType === 'BUYBACK_PURCHASE') && (
            <GlassCard className='mb-6 bg-blue-50 border-blue-200'>
              <p className='text-blue-800 font-semibold text-sm mb-3 flex items-center gap-2'><FaExchangeAlt /> Exchange Details</p>
              <div className='space-y-3'>
                <div>
                  <label className='block text-slate-700 font-semibold mb-2 text-sm'>New Jewellery Subtotal (₹)</label>
                  <GlassInput type='number' step='0.01' value={exchangeSubtotal} onChange={e => setExchangeSubtotal(e.target.value)}
                    placeholder='Total cost of new jewellery the customer wants' />
                </div>
                <div>
                  <label className='block text-slate-700 font-semibold mb-2 text-sm'>Link to Existing Order ID (optional)</label>
                  <GlassInput value={linkedOrderId} onChange={e => setLinkedOrderId(e.target.value)} placeholder='ORD-...' />
                </div>
              </div>
            </GlassCard>
          )}

          <div className='flex gap-3'>
            <GlassButton variant='secondary' className='flex-1' onClick={() => nav(-1)}><FaArrowLeft className='inline mr-1' /> Back</GlassButton>
            <GlassButton variant='primary' className='flex-1' disabled={!canProceed()} onClick={() => nav(1)}>
              Next: Add Items <FaArrowRight className='inline ml-1' />
            </GlassButton>
          </div>
        </GlassPanel>
      )}

      {/* STEP 2: Items */}
      {step === 2 && (
        <GlassPanel className='p-6 md:p-8'>
          <h2 className='text-2xl font-bold mb-1 flex items-center gap-2'><FaGem className='text-amber-500' /> Old Gold Items</h2>
          <p className='text-slate-500 text-sm mb-6'>Add all old gold items the customer is submitting</p>

          <StepHint steps={[
            'Enter a name for each item (e.g., Gold Chain, Old Ring).',
            'Gross Weight = total weight including stones/other material.',
            'Net Weight = gold weight AFTER removing stones/dirt. Stone weight is calculated automatically (Gross − Net).',
            'Select the estimated purity (karat) of each item if known.',
            'Use "Add Another Item" for multiple pieces. Remove items with the trash icon.',
            'Tap "Next: Gold Testing" once all items are added.',
          ]} />

          <div className='space-y-4 mb-6'>
            {items.map((item, idx) => (
              <GlassCard key={idx} className='bg-slate-50 border-slate-200 relative'>
                <div className='flex items-center justify-between mb-3'>
                  <h3 className='font-bold text-slate-900'>Item {idx + 1}</h3>
                  {items.length > 1 && (
                    <button onClick={() => removeItem(idx)} className='text-red-400 hover:text-red-600 p-1'><FaTrash /></button>
                  )}
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                  <div>
                    <label className='block text-slate-600 text-xs font-semibold mb-1'>Item Name *</label>
                    <GlassInput value={item.itemName} onChange={e => updateItem(idx, 'itemName', e.target.value)}
                      placeholder='e.g., Gold Chain, Old Ring' />
                  </div>
                  <div>
                    <label className='block text-slate-600 text-xs font-semibold mb-1'>Item Type</label>
                    <GlassSelect value={item.itemType} onChange={e => updateItem(idx, 'itemType', e.target.value)}>
                      {ITEM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </GlassSelect>
                  </div>
                  <div>
                    <label className='block text-slate-600 text-xs font-semibold mb-1 flex items-center gap-1'><FaWeight className='text-amber-500' /> Gross Weight (g) *</label>
                    <GlassInput type='number' step='0.001' min='0' value={item.grossWeight}
                      onChange={e => updateItem(idx, 'grossWeight', e.target.value)} placeholder='0.000' />
                  </div>
                  <div>
                    <label className='block text-slate-600 text-xs font-semibold mb-1 flex items-center gap-1'><FaWeight className='text-green-500' /> Net Weight (g) *</label>
                    <GlassInput type='number' step='0.001' min='0' value={item.netWeight}
                      onChange={e => updateItem(idx, 'netWeight', e.target.value)} placeholder='0.000' />
                  </div>
                  <div className='flex items-center gap-2 pt-4'>
                    <input type='checkbox' checked={item.hallmark} onChange={e => updateItem(idx, 'hallmark', e.target.checked)}
                      className='w-4 h-4 accent-amber-500' />
                    <label className='text-sm text-slate-700 font-medium flex items-center gap-1'><FaCertificate className='text-amber-500' /> Hallmark</label>
                  </div>
                  {item.hallmark && (
                    <div>
                      <label className='block text-slate-600 text-xs font-semibold mb-1'>Hallmark Number</label>
                      <GlassInput value={item.hallmarkNumber} onChange={e => updateItem(idx, 'hallmarkNumber', e.target.value)} placeholder='Hallmark ID' />
                    </div>
                  )}
                  <div>
                    <label className='block text-slate-600 text-xs font-semibold mb-1'>Estimated Purity</label>
                    <GlassSelect value={item.estimatedPurity} onChange={e => updateItem(idx, 'estimatedPurity', e.target.value)}>
                      {KARAT_VALUES.map(k => <option key={k} value={k}>{k}</option>)}
                    </GlassSelect>
                  </div>
                  <div className='md:col-span-2'>
                    <label className='block text-slate-600 text-xs font-semibold mb-1'>Remarks</label>
                    <GlassInput value={item.remarks} onChange={e => updateItem(idx, 'remarks', e.target.value)} placeholder='Any notes about this item' />
                  </div>
                </div>
                <div className='mt-2 text-xs text-slate-500'>
                  Net Weight: {fmtW(parseFloat(item.netWeight) || 0)}g
                </div>
              </GlassCard>
            ))}
          </div>

          <GlassButton variant='secondary' className='mb-6' onClick={addItem}><FaPlus className='inline mr-1' /> Add Another Item</GlassButton>

          <div className='bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-sm text-amber-800'>
            <strong>Totals:</strong> Gross: {fmtW(totals.totalGross)}g · Net: {fmtW(totals.totalNet)}g · Items: {items.length}
          </div>

          <div className='flex gap-3'>
            <GlassButton variant='secondary' className='flex-1' onClick={() => nav(-1)}><FaArrowLeft className='inline mr-1' /> Back</GlassButton>
            <GlassButton variant='primary' className='flex-1' disabled={!canProceed()} onClick={() => nav(1)}>
              Next: Gold Testing <FaArrowRight className='inline ml-1' />
            </GlassButton>
          </div>
        </GlassPanel>
      )}

      {/* STEP 3: Testing */}
      {step === 3 && (
        <GlassPanel className='p-6 md:p-8'>
          <h2 className='text-2xl font-bold mb-1 flex items-center gap-2'><FaFlask className='text-purple-500' /> Gold Testing</h2>
          <p className='text-slate-500 text-sm mb-6'>Record test results for the submitted items</p>

          <StepHint steps={[
            'Select the testing method used (XRF machine, Karat Meter, Acid test, etc.).',
            'Enter the name of the staff member who did the test.',
            'Result Purity (%) is the measured gold purity (e.g., 91.60 for 22K). This is essential.',
            'Optionally note the machine number and karat value (22K, 24K, etc.).',
            'Use "Add Another Test" for multiple measurements.',
            'The average purity and fine gold weight are shown automatically.',
          ]} />

          <div className='space-y-4 mb-6'>
            {tests.map((test, idx) => (
              <GlassCard key={idx} className='bg-slate-50 border-slate-200 relative'>
                <div className='flex items-center justify-between mb-3'>
                  <h3 className='font-bold text-slate-900'>Test {idx + 1}</h3>
                  {tests.length > 1 && (
                    <button onClick={() => removeTest(idx)} className='text-red-400 hover:text-red-600 p-1'><FaTrash /></button>
                  )}
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                  <div>
                    <label className='block text-slate-600 text-xs font-semibold mb-1'>Testing Method *</label>
                    <GlassSelect value={test.testingMethod} onChange={e => updateTest(idx, 'testingMethod', e.target.value)}>
                      {TEST_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </GlassSelect>
                  </div>
                  <div>
                    <label className='block text-slate-600 text-xs font-semibold mb-1'>Tested By *</label>
                    <GlassInput value={test.testedBy} onChange={e => updateTest(idx, 'testedBy', e.target.value)} placeholder='Staff name' />
                  </div>
                  <div>
                    <label className='block text-slate-600 text-xs font-semibold mb-1'>Machine Number</label>
                    <GlassInput value={test.machineNumber} onChange={e => updateTest(idx, 'machineNumber', e.target.value)} placeholder='Optional' />
                  </div>
                  <div>
                    <label className='block text-slate-600 text-xs font-semibold mb-1'>Result Purity (%) *</label>
                    <GlassInput type='number' step='0.01' min='0' max='100' value={test.resultPurity}
                      onChange={e => {
                        const raw = e.target.value;
                        if (raw === '') { updateTest(idx, 'resultPurity', ''); return; }
                        const n = parseFloat(raw);
                        // Purity is a percentage — never let a fat-fingered value
                        // (e.g. 242344) reach the valuation maths.
                        if (Number.isFinite(n) && n > 100) { updateTest(idx, 'resultPurity', '100'); return; }
                        updateTest(idx, 'resultPurity', raw);
                      }} placeholder='e.g., 91.60' />
                  </div>
                  <div>
                    <label className='block text-slate-600 text-xs font-semibold mb-1'>Karat Value</label>
                    <GlassSelect value={test.karatValue} onChange={e => updateTest(idx, 'karatValue', e.target.value)}>
                      {KARAT_VALUES.map(k => <option key={k} value={k}>{k}</option>)}
                    </GlassSelect>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>

          <GlassButton variant='secondary' className='mb-6' onClick={addTest}><FaPlus className='inline mr-1' /> Add Another Test</GlassButton>

          <GlassCard className='mb-6 bg-purple-50 border-purple-200'>
            <p className='text-sm text-purple-800'>
              <strong>Average Purity:</strong> {totals.avgPurity.toFixed(2)}% · <strong>Fine Gold Weight:</strong> {fmtW(totals.fineGold)}g
            </p>
          </GlassCard>

          <div className='flex gap-3'>
            <GlassButton variant='secondary' className='flex-1' onClick={() => nav(-1)}><FaArrowLeft className='inline mr-1' /> Back</GlassButton>
            <GlassButton variant='primary' className='flex-1' disabled={!canProceed()} onClick={() => nav(1)}>
              Next: Valuation <FaArrowRight className='inline ml-1' />
            </GlassButton>
          </div>
        </GlassPanel>
      )}

      {/* STEP 4: Valuation */}
      {step === 4 && (
        <GlassPanel className='p-6 md:p-8'>
          <h2 className='text-2xl font-bold mb-1 flex items-center gap-2'><FaCalculator className='text-blue-500' /> Valuation</h2>
          <p className='text-slate-500 text-sm mb-6'>The fine (pure) gold is valued at today's 24 Carat market rate.</p>

          <GlassCard className='mb-4 bg-slate-50 border-slate-200'>
            <p className='text-sm text-slate-700 leading-relaxed'>
              <strong className='text-amber-700'>How it works:</strong> Your old gold weight is measured in grams, then reduced to its
              pure gold (fine) content using the tested purity. This fine gold is then multiplied by today's 24 Carat gold rate
              to get the buyback value. Store charges and deductions are subtracted at the end.
            </p>
          </GlassCard>

          {/* Manual Gold Rate Override */}
          <GlassCard className='mb-6 bg-blue-50 border-blue-200'>
            <p className='text-blue-800 font-semibold text-sm mb-3 flex items-center gap-2'><FaCog /> Today's Gold Rate (per gram) — 24 Carat</p>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3 mb-3'>
              <div>
                <label className='block text-blue-700 text-xs font-semibold mb-1'>24 Carat Rate (₹/gram) *</label>
                <GlassInput type='number' step='0.01' min='0' value={manualRate24K}
                  onChange={e => {
                    setManualRate24K(e.target.value);
                    setUseManualRate(true);
                  }}
                  placeholder='Enter today 24K gold price' />
                <p className='text-[10px] text-blue-500 mt-0.5'>Standard market (24 carat / pure gold) price per gram.</p>
              </div>
              <div>
                <label className='block text-blue-700 text-xs font-semibold mb-1'>Store Charges</label>
                <div className='flex gap-0'>
                  <GlassInput type='number' step='0.01' min='0' value={manualBuybackDiscount}
                    onChange={e => { setManualBuybackDiscount(e.target.value); setUseManualRate(true); }}
                    placeholder={discountType === 'percent' ? 'e.g., 5' : 'e.g., 200'}
                    className='flex-1 rounded-r-none border-r-0' />
                  <button
                    type='button'
                    className={`px-3 py-2 border-2 text-sm font-bold rounded-r-lg transition-all ${
                      discountType === 'flat' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-600 border-slate-200 hover:border-amber-300'
                    }`}
                    onClick={() => { setDiscountType('flat'); setUseManualRate(true); }}
                  >₹</button>
                  <button
                    type='button'
                    className={`px-3 py-2 border-2 text-sm font-bold rounded-r-lg -ml-px transition-all ${
                      discountType === 'percent' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-600 border-slate-200 hover:border-amber-300'
                    }`}
                    onClick={() => { setDiscountType('percent'); setUseManualRate(true); }}
                  >%</button>
                </div>
                <p className='text-[10px] text-blue-500 mt-0.5'>
                  {discountType === 'flat'
                    ? `Flat ₹${fmt(rawStoreCharges)} deducted from the value`
                    : `${manualBuybackDiscount || 0}% of the value = ₹${fmt(totals.storeCharges)}`
                  }
                </p>
              </div>
            </div>
            <div className='flex items-end'>
              <div className='bg-purple-50 border border-purple-200 rounded-lg p-3 w-full text-center'>
                <p className='text-xs text-purple-600 font-medium'>24 Carat Market Rate</p>
                <p className='text-xl font-bold text-purple-800'>₹{fmt(totals.marketRate)}<span className='text-xs font-normal'>/g</span></p>
                <p className='text-[10px] text-purple-500'>Fine gold valued at today's 24K rate</p>
              </div>
            </div>
          </GlassCard>

          {/* Big Amount Hero */}
          <div className='bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 rounded-2xl p-6 mb-6 text-white shadow-xl'>
            <p className='text-amber-100 text-sm font-medium uppercase tracking-wide mb-1'>Final Buyback Value</p>
            <p className='text-4xl md:text-5xl font-bold flex items-center gap-2 mb-2'>
              <FaRupeeSign className='text-amber-200' />{fmt(totals.finalAmount)}
            </p>
            <div className='text-amber-200 text-xs space-y-0.5'>
              <p>{fmtW(totals.totalNet)}g net × {totals.avgPurity.toFixed(1)}% purity = {fmtW(totals.fineGold)}g fine gold</p>
              <p>{fmtW(totals.fineGold)}g × ₹{fmt(totals.marketRate)}/g = ₹{fmt(totals.goldValue)}</p>
              {totals.storeCharges > 0 && <p>- ₹{fmt(totals.storeCharges)} store charges</p>}
              {totals.totalDeductions > 0 && <p>- ₹{fmt(totals.totalDeductions)} deductions</p>}
              <p className='text-white font-semibold'>= ₹{fmt(totals.finalAmount)}</p>
            </div>
          </div>

          <div className='space-y-3 mb-6'>
            <GlassCard className='bg-white'>
              <p className='text-xs text-slate-500 font-medium uppercase mb-2'>Gold Rate (per gram)</p>
              <div className='grid grid-cols-2 md:grid-cols-2 gap-3 text-center'>
                <div className='bg-blue-50 rounded-lg p-2'><p className='text-xs text-blue-600'>24 Carat Market Rate</p><p className='font-bold text-blue-800'>₹{fmt(totals.rate24K)}<span className='text-xs font-normal'>/g</span></p></div>
                <div className='bg-purple-50 rounded-lg p-2'><p className='text-xs text-purple-600'>Store Charges</p><p className='font-bold text-purple-800'>₹{fmt(totals.storeCharges)}</p></div>
              </div>
            </GlassCard>

            <GlassCard className='bg-white'>
              <p className='text-xs text-slate-500 font-medium uppercase mb-2'>Weight Breakdown</p>
              <div className='space-y-1 text-sm'>
                <div className='flex justify-between'><span className='text-slate-600'>Total Gross Weight</span><span className='font-semibold'>{fmtW(totals.totalGross)}g</span></div>
                <div className='flex justify-between'><span className='text-slate-600'>Total Net Weight</span><span className='font-semibold text-green-700'>{fmtW(totals.totalNet)}g</span></div>
                {totals.totalStone > 0 && (
                  <div className='flex justify-between'><span className='text-slate-600'>Stone (Gross − Net)</span><span className='font-semibold text-red-600'>-{fmtW(totals.totalStone)}g</span></div>
                )}
                <div className='flex justify-between'><span className='text-slate-600'>Average Purity</span><span className='font-semibold text-purple-700'>{totals.avgPurity.toFixed(2)}%</span></div>
                <div className='flex justify-between border-t pt-1'><span className='font-bold'>Fine Gold Weight</span><span className='font-bold text-amber-700'>{fmtW(totals.fineGold)}g</span></div>
              </div>
            </GlassCard>

            <GlassCard className='bg-white'>
              <p className='text-xs text-slate-500 font-medium uppercase mb-2'>Value Calculation</p>
              <div className='space-y-1 text-sm'>
                <div className='flex justify-between'><span className='text-slate-600'>Fine Gold × 24K Rate</span><span className='font-medium'>{fmtW(totals.fineGold)}g × ₹{fmt(totals.marketRate)}</span></div>
                <div className='flex justify-between'><span className='text-slate-600'>Gross Gold Value</span><span className='font-semibold'>₹{fmt(totals.goldValue)}</span></div>
                {totals.storeCharges > 0 && (
                  <div className='flex justify-between'><span className='text-slate-600'>Store Charges</span><span className='font-semibold text-red-600'>-₹{fmt(totals.storeCharges)}</span></div>
                )}
                {totals.totalDeductions > 0 && (
                  <div className='flex justify-between'><span className='text-slate-600'>Deductions</span><span className='font-semibold text-red-600'>-₹{fmt(totals.totalDeductions)}</span></div>
                )}
                <div className='flex justify-between border-t pt-1 text-lg'><span className='font-bold'>Final Amount</span><span className='font-bold text-green-700'>₹{fmt(totals.finalAmount)}</span></div>
              </div>
            </GlassCard>

            {(txnType === 'EXCHANGE' || txnType === 'BUYBACK_PURCHASE') && exchangeSubtotal && (
              <GlassCard className='bg-blue-50 border-blue-200'>
                <p className='text-xs text-blue-700 font-medium uppercase mb-2'>Exchange Balance</p>
                <div className='space-y-1 text-sm'>
                  <div className='flex justify-between'><span className='text-blue-600'>New Jewellery Cost</span><span className='font-semibold'>₹{fmt(totals.exchangeBalance + totals.finalAmount)}</span></div>
                  <div className='flex justify-between'><span className='text-blue-600'>Old Gold Credit</span><span className='font-semibold text-green-700'>-₹{fmt(totals.finalAmount)}</span></div>
                  <div className='flex justify-between border-t pt-1 text-lg'>
                    <span className='font-bold'>{totals.exchangeBalance >= 0 ? 'Balance Payable' : 'Refund Due'}</span>
                    <span className={`font-bold ${totals.exchangeBalance >= 0 ? 'text-amber-700' : 'text-green-700'}`}>₹{fmt(Math.abs(totals.exchangeBalance))}</span>
                  </div>
                </div>
              </GlassCard>
            )}
          </div>

          <div className='flex gap-3'>
            <GlassButton variant='secondary' className='flex-1' onClick={() => nav(-1)}><FaArrowLeft className='inline mr-1' /> Back</GlassButton>
            <GlassButton variant='primary' className='flex-1' onClick={() => nav(1)}>
              Next: Deductions <FaArrowRight className='inline ml-1' />
            </GlassButton>
          </div>
        </GlassPanel>
      )}

      {/* STEP 5: Deductions */}
      {step === 5 && (
        <GlassPanel className='p-6 md:p-8'>
          <h2 className='text-2xl font-bold mb-1 flex items-center gap-2'><FaClipboardCheck className='text-red-500' /> Deductions</h2>
          <p className='text-slate-500 text-sm mb-6'>Add itemized deductions (if any)</p>

          <GlassCard className='mb-6 bg-amber-50 border-amber-200'>
            <p className='text-sm text-amber-800'>Gold Value: <strong>₹{fmt(totals.goldValue)}</strong> · Store Charges: <strong className='text-red-600'>₹{fmt(totals.storeCharges)}</strong> · Deductions: <strong className='text-red-600'>₹{fmt(totals.totalDeductions)}</strong> · Net: <strong className='text-green-700'>₹{fmt(totals.finalAmount)}</strong></p>
          </GlassCard>

          <div className='space-y-3 mb-4'>
            {deductions.map((d, idx) => (
              <GlassCard key={idx} className='bg-slate-50 border-slate-200 relative'>
                <div className='flex items-center justify-between mb-2'>
                  <h4 className='font-semibold text-sm'>Deduction {idx + 1}</h4>
                  <button onClick={() => removeDeduction(idx)} className='text-red-400 hover:text-red-600 p-1'><FaTrash /></button>
                </div>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                  <div>
                    <label className='block text-slate-600 text-xs font-semibold mb-1'>Type</label>
                    <GlassSelect value={d.deductionType} onChange={e => updateDeduction(idx, 'deductionType', e.target.value)}>
                      {DEDUCTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </GlassSelect>
                  </div>
                  <div>
                    <label className='block text-slate-600 text-xs font-semibold mb-1'>Reason</label>
                    <GlassInput value={d.reason} onChange={e => updateDeduction(idx, 'reason', e.target.value)} placeholder='Description' />
                  </div>
                  <div>
                    <label className='block text-slate-600 text-xs font-semibold mb-1'>Amount (₹)</label>
                    <GlassInput type='number' step='0.01' min='0' value={d.amount} onChange={e => updateDeduction(idx, 'amount', e.target.value)} placeholder='0.00' />
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>

          <GlassButton variant='secondary' className='mb-6' onClick={addDeduction}><FaPlus className='inline mr-1' /> Add Deduction</GlassButton>

          <div className='mb-6'>
            <label className='block text-slate-700 font-semibold mb-2 text-sm'>Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder='Any additional notes...'
              className='w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all' rows={3} />
          </div>

          <div className='flex gap-3'>
            <GlassButton variant='secondary' className='flex-1' onClick={() => nav(-1)}><FaArrowLeft className='inline mr-1' /> Back</GlassButton>
            <GlassButton variant='primary' className='flex-1' onClick={() => nav(1)}>
              Next: Review <FaArrowRight className='inline ml-1' />
            </GlassButton>
          </div>
        </GlassPanel>
      )}

      {/* STEP 6: Review & Submit */}
      {step === 6 && (
        <GlassPanel className='p-6 md:p-8'>
          <h2 className='text-2xl font-bold mb-1 flex items-center gap-2'><FaEye className='text-blue-500' /> Review & Submit</h2>
          <p className='text-slate-500 text-sm mb-6'>Verify everything before submitting</p>

          <div className='bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl p-5 mb-6 text-white shadow-lg'>
            <p className='text-amber-100 text-xs font-medium uppercase mb-1'>Final Buyback Value</p>
            <p className='text-3xl font-bold flex items-center gap-2'><FaRupeeSign />{fmt(totals.finalAmount)}</p>
          </div>

          <div className='space-y-3 mb-6'>
            <GlassCard className='bg-slate-50'>
              <p className='text-xs text-slate-500 font-medium uppercase mb-2'>Customer</p>
              <p className='font-bold text-slate-900'>{customer?.customerName || custName}</p>
              <p className='text-sm text-slate-600'>{phone}</p>
            </GlassCard>

            <GlassCard className='bg-slate-50'>
              <p className='text-xs text-slate-500 font-medium uppercase mb-2'>Transaction</p>
              <div className='space-y-1 text-sm'>
                <div className='flex justify-between'><span className='text-slate-600'>Type</span><span className='font-semibold'>{txnType === 'BUYBACK_PURCHASE' ? 'Buyback + Purchase' : txnType}</span></div>
                <div className='flex justify-between'><span className='text-slate-600'>Items</span><span className='font-semibold'>{items.length}</span></div>
                <div className='flex justify-between'><span className='text-slate-600'>Total Gross Weight</span><span className='font-semibold'>{fmtW(totals.totalGross)}g</span></div>
                <div className='flex justify-between'><span className='text-slate-600'>Total Net Weight</span><span className='font-semibold text-green-700'>{fmtW(totals.totalNet)}g</span></div>
                <div className='flex justify-between'><span className='text-slate-600'>Fine Gold</span><span className='font-semibold text-amber-700'>{fmtW(totals.fineGold)}g</span></div>
              </div>
            </GlassCard>

            <GlassCard className='bg-slate-50'>
              <p className='text-xs text-slate-500 font-medium uppercase mb-2'>Calculation</p>
              <div className='space-y-1 text-sm'>
                <div className='flex justify-between'><span className='text-slate-600'>24 Carat Market Rate</span><span className='font-semibold'>₹{fmt(totals.marketRate)}/g</span></div>
                <div className='flex justify-between'><span className='text-slate-600'>Gold Value</span><span className='font-semibold'>₹{fmt(totals.goldValue)}</span></div>
                {totals.storeCharges > 0 && (
                  <div className='flex justify-between'><span className='text-slate-600'>Store Charges</span><span className='font-semibold text-red-600'>-₹{fmt(totals.storeCharges)}</span></div>
                )}
                {totals.totalDeductions > 0 && (
                  <div className='flex justify-between'><span className='text-slate-600'>Deductions</span><span className='font-semibold text-red-600'>-₹{fmt(totals.totalDeductions)}</span></div>
                )}
                <div className='flex justify-between border-t pt-1'><span className='font-bold'>Final Amount</span><span className='font-bold text-green-700'>₹{fmt(totals.finalAmount)}</span></div>
              </div>
            </GlassCard>

            {deductions.length > 0 && (
              <GlassCard className='bg-red-50 border-red-200'>
                <p className='text-xs text-red-700 font-medium uppercase mb-2'>Deductions Breakdown</p>
                {deductions.filter(d => d.reason && parseFloat(d.amount) > 0).map((d, i) => (
                  <div key={i} className='flex justify-between text-sm'>
                    <span className='text-red-600'>{d.deductionType}: {d.reason}</span>
                    <span className='font-semibold text-red-700'>-₹{fmt(parseFloat(d.amount) || 0)}</span>
                  </div>
                ))}
              </GlassCard>
            )}

            {(txnType === 'EXCHANGE' || txnType === 'BUYBACK_PURCHASE') && totals.exchangeBalance !== 0 && (
              <GlassCard className='bg-blue-50 border-blue-200'>
                <p className='text-xs text-blue-700 font-medium uppercase mb-2'>Exchange</p>
                <div className='flex justify-between text-sm'>
                  <span className='text-blue-600'>{totals.exchangeBalance >= 0 ? 'Balance Payable' : 'Refund Due'}</span>
                  <span className='font-bold text-blue-800'>₹{fmt(Math.abs(totals.exchangeBalance))}</span>
                </div>
              </GlassCard>
            )}

            <GlassCard className='bg-slate-50'>
              <p className='text-xs text-slate-500 font-medium uppercase mb-2'>Staff Confirmation</p>
              <label className='flex items-start gap-3 p-3 bg-amber-50 border-2 border-amber-300 rounded-xl cursor-pointer'>
                <input type='checkbox' className='w-5 h-5 mt-0.5 accent-amber-600' checked={!!canProceed()}
                  readOnly />
                <div>
                  <p className='font-bold text-amber-900 flex items-center gap-2'><FaClipboardCheck className='text-amber-600' /> Staff Verified</p>
                  <p className='text-xs text-amber-700 mt-1'>I confirm that the customer has verified the purity, weight, deductions, and final amount.</p>
                </div>
              </label>
            </GlassCard>
          </div>

          <div className='flex gap-3'>
            <GlassButton variant='secondary' className='flex-1' onClick={() => nav(-1)}><FaArrowLeft className='inline mr-1' /> Back</GlassButton>
            <GlassButton variant='secondary' className='flex-1' onClick={() => setShowPreview(true)}>
              <FaEye className='inline mr-1' /> Preview
            </GlassButton>
            <GlassButton variant='primary' className='flex-1' disabled={submitting} onClick={handleSubmit}>
              {submitting ? 'Submitting...' : <><FaCheckCircle className='inline mr-1' /> {isEdit ? 'Save Changes' : 'Submit Transaction'}</>}
            </GlassButton>
          </div>
        </GlassPanel>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className='fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4' onClick={() => setShowPreview(false)}>
          <div className='bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl' onClick={e => e.stopPropagation()}>
            <div className='sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl'>
              <h3 className='font-bold text-slate-900 flex items-center gap-2'><FaEye className='text-blue-500' /> Transaction Preview</h3>
              <button onClick={() => setShowPreview(false)} className='text-slate-400 hover:text-slate-600'><FaTimes /></button>
            </div>
            <div className='p-6 space-y-4'>
              <div className='bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl p-5 text-white text-center'>
                <p className='text-amber-100 text-xs uppercase font-medium'>Final Buyback Value</p>
                <p className='text-4xl font-bold flex items-center gap-2 justify-center mt-1'><FaRupeeSign />{fmt(totals.finalAmount)}</p>
                <p className='text-amber-200 text-xs mt-1'>{fmtW(totals.fineGold)}g fine gold × ₹{fmt(totals.marketRate)}/g</p>
              </div>

              <div className='bg-slate-50 rounded-xl p-4 border border-slate-200'>
                <p className='text-xs text-slate-500 uppercase font-semibold mb-2'>Customer</p>
                <p className='font-bold text-slate-900'>{customer?.customerName || custName}</p>
                <p className='text-sm text-slate-600'>{customer?.cellNumber || phone}</p>
              </div>

              <div className='bg-slate-50 rounded-xl p-4 border border-slate-200'>
                <p className='text-xs text-slate-500 uppercase font-semibold mb-2'>Items ({items.length})</p>
                {items.map((it, i) => (
                  <div key={i} className='flex justify-between text-sm py-1 border-b border-slate-100 last:border-0'>
                    <span className='text-slate-700'>{it.itemName} <span className='text-xs text-slate-400'>({it.itemType})</span></span>
                    <span className='font-medium text-slate-900'>{fmtW(parseFloat(it.netWeight) || 0)}g net</span>
                  </div>
                ))}
              </div>

              <div className='bg-slate-50 rounded-xl p-4 border border-slate-200'>
                <p className='text-xs text-slate-500 uppercase font-semibold mb-2'>Rates & Calculation</p>
                <div className='space-y-1 text-sm'>
                  <div className='flex justify-between'><span className='text-slate-600'>24K Rate</span><span className='font-semibold'>₹{fmt(totals.rate24K)}/g</span></div>
                  <div className='flex justify-between'><span className='text-slate-600'>Fine Gold</span><span className='font-semibold text-amber-700'>{fmtW(totals.fineGold)}g</span></div>
                  <div className='flex justify-between'><span className='text-slate-600'>Gold Value</span><span className='font-semibold'>₹{fmt(totals.goldValue)}</span></div>
                  {totals.storeCharges > 0 && (
                    <div className='flex justify-between'><span className='text-slate-600'>Store Charges</span><span className='font-semibold text-red-600'>-₹{fmt(totals.storeCharges)}</span></div>
                  )}
                  {totals.totalDeductions > 0 && (
                    <div className='flex justify-between'><span className='text-slate-600'>Deductions</span><span className='font-semibold text-red-600'>-₹{fmt(totals.totalDeductions)}</span></div>
                  )}
                  <div className='flex justify-between border-t pt-1'><span className='font-bold'>Final Amount</span><span className='font-bold text-green-700'>₹{fmt(totals.finalAmount)}</span></div>
                </div>
              </div>

              <div className='flex gap-3 pt-2'>
                <GlassButton variant='secondary' className='flex-1' onClick={() => setShowPreview(false)}><FaEdit className='inline mr-1' /> Edit</GlassButton>
                <GlassButton variant='primary' className='flex-1' disabled={submitting} onClick={async () => { setShowPreview(false); await handleSubmit(); }}>
                  {submitting ? 'Submitting...' : <><FaCheckCircle className='inline mr-1' /> {isEdit ? 'Save Changes' : 'Submit'}</>}
                </GlassButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// DETAIL VIEW
// ═══════════════════════════════════════════════════════════

const DetailView: React.FC<{ onBack: () => void; transactionId?: string; onEdit?: (id: string) => void }> = ({ onBack, transactionId, onEdit }) => {
  const [txn, setTxn] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  // Manual payment figures (typed by hand, never calculated by the CRM).
  const [payPaid, setPayPaid] = useState('');
  const [payBalance, setPayBalance] = useState('');
  const [paySaving, setPaySaving] = useState(false);

  const id = transactionId || '';

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await ogService.getTransactionById(id);
      setTxn(res.data.transaction || res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Seed the payment inputs whenever a transaction finishes loading.
  useEffect(() => {
    if (txn) {
      setPayPaid(String(txn.amountPaid ?? 0));
      setPayBalance(String(txn.balanceAmount ?? txn.finalAmount ?? 0));
    }
  }, [txn]);

  const handleSavePayment = async () => {
    if (!id) return;
    setPaySaving(true);
    try {
      await ogService.setPayment(id, {
        amountPaid: Number(payPaid) || 0,
        balanceAmount: Number(payBalance) || 0,
      });
      load();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to save payment');
    } finally {
      setPaySaving(false);
    }
  };

  const handleAdvanceStatus = async (newStatus: string) => {
    if (!id) return;
    setActionLoading(true);
    try {
      await ogService.updateStatus(id, newStatus);
      load();
    } catch (e: any) { alert(e.response?.data?.error || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const handleManagerApprove = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await ogService.approveTransaction(id, { managerApproved: true });
      load();
    } catch (e: any) { alert(e.response?.data?.error || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const handleCustomerApprove = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await ogService.customerApprove(id, { customerApproved: true, approvalMethod: 'SIGNATURE' });
      load();
    } catch (e: any) { alert(e.response?.data?.error || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const handleCancel = async () => {
    if (!id || !window.confirm('Cancel this transaction?')) return;
    setActionLoading(true);
    try {
      await ogService.updateStatus(id, 'CANCELLED');
      load();
    } catch (e: any) { alert(e.response?.data?.error || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const generatePDF = (t: any) => {
    const w = window.open('', '_blank');
    if (!w) return;
    // buybackDiscount/store charges are not persisted on the row; derive them so
    // Gold Value - Store Charges - Deductions always reconciles to Final Amount.
    const storeCharges = Math.round((Number(t.totalGoldValue || 0) - Number(t.totalDeductions || 0) - Number(t.finalAmount || 0)) * 100) / 100;
    w.document.write(`<!DOCTYPE html><html><head><title>${t.transactionNumber}</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; color: #1e293b; font-size: 13px; }
      .header { text-align: center; border-bottom: 3px solid #f59e0b; padding-bottom: 15px; margin-bottom: 20px; }
      .header h1 { font-size: 22px; color: #f59e0b; margin-bottom: 4px; }
      .header p { color: #64748b; font-size: 12px; }
      .badge { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; }
      .badge-draft { background: #f1f5f9; color: #475569; }
      .badge-testing { background: #f3e8ff; color: #7c3aed; }
      .badge-valuation { background: #dbeafe; color: #2563eb; }
      .badge-pending { background: #fef3c7; color: #d97706; }
      .badge-approved { background: #dcfce7; color: #16a34a; }
      .badge-completed { background: #22c55e; color: #fff; }
      .badge-cancelled { background: #fee2e2; color: #dc2626; }
      .section { margin-bottom: 16px; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; }
      .section h3 { font-size: 13px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
      .row { display: flex; justify-content: space-between; padding: 4px 0; }
      .row span:first-child { color: #64748b; }
      .row span:last-child { font-weight: 600; }
      .row.total { border-top: 2px solid #f59e0b; margin-top: 6px; padding-top: 8px; font-size: 16px; }
      .row.total span:last-child { color: #16a34a; font-size: 18px; }
      .item-row { background: #f8fafc; padding: 8px 10px; border-radius: 6px; margin-bottom: 4px; }
      .item-row .name { font-weight: 600; }
      .item-row .meta { color: #64748b; font-size: 11px; }
      .hero { background: linear-gradient(135deg, #f59e0b, #eab308); color: white; text-align: center; padding: 20px; border-radius: 10px; margin-bottom: 20px; }
      .hero .amount { font-size: 32px; font-weight: 700; }
      .hero .sub { font-size: 12px; opacity: 0.85; }
      .footer { text-align: center; margin-top: 24px; padding-top: 12px; border-top: 1px solid #e2e8f0; color: #94a3b8; font-size: 11px; }
      @media print { body { padding: 15px; } }
    </style></head><body>
    <div class="header">
      <h1>ApniCRM - Old Gold Transaction</h1>
      <p>${t.transactionNumber} · ${t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</p>
    </div>
    <div class="hero">
      <div class="sub">Final Buyback Value</div>
      <div class="amount">\u20B9${fmt(t.finalAmount || 0)}</div>
      <div class="sub">${fmtW(t.totalFineGold || 0)}g fine gold @ \u20B9${fmt(t.buybackRate || 0)}/g</div>
    </div>
    <div class="section">
      <h3>Customer</h3>
      <div class="row"><span>Name</span><span>${t.customer?.customerName || 'N/A'}</span></div>
      <div class="row"><span>Phone</span><span>${t.customer?.cellNumber || 'N/A'}</span></div>
      <div class="row"><span>Transaction Type</span><span>${t.transactionType}</span></div>
      <div class="row"><span>Status</span><span>${t.status}</span></div>
    </div>
    <div class="section">
      <h3>Items</h3>
      ${(t.items || []).map((it: any) => `
        <div class="item-row">
          <div class="name">${it.itemName} <span class="meta">(${it.itemType}, qty: ${it.quantity})</span></div>
          <div class="meta">Gross: ${fmtW(it.grossWeight)}g · Net: ${fmtW(it.netWeight)}g · Purity: ${it.estimatedPurity || 'N/A'} ${it.hallmark ? '· Hallmarked' : ''}</div>
        </div>
      `).join('')}
    </div>
    ${(t.tests || []).length > 0 ? `<div class="section"><h3>Test Results</h3>${t.tests.map((t: any) => `<div class="row"><span>${t.testingMethod} (${t.testedBy})</span><span>${t.resultPurity}% ${t.karatValue || ''}</span></div>`).join('')}</div>` : ''}
    <div class="section">
      <h3>Valuation</h3>
      <div class="row"><span>Total Gross Weight</span><span>${fmtW(t.totalGrossWeight || 0)}g</span></div>
      <div class="row"><span>Total Net Weight</span><span>${fmtW(t.totalNetWeight || 0)}g</span></div>
      <div class="row"><span>Fine Gold Weight</span><span>${fmtW(t.totalFineGold || 0)}g</span></div>
      <div class="row"><span>24K Rate</span><span>\u20B9${fmt(t.goldRate24K || 0)}/g</span></div>
      <div class="row"><span>24K Market Rate</span><span>\u20B9${fmt(t.buybackRate || 0)}/g</span></div>
      <div class="row"><span>Gold Value</span><span>\u20B9${fmt(t.totalGoldValue || 0)}</span></div>
      ${storeCharges > 0 ? `<div class="row"><span>Store / Buyback Charges</span><span style="color:#dc2626">-\u20B9${fmt(storeCharges)}</span></div>` : ''}
      <div class="row"><span>Deductions</span><span style="color:#dc2626">-\u20B9${fmt(t.totalDeductions || 0)}</span></div>
      <div class="row total"><span>Final Amount</span><span>\u20B9${fmt(t.finalAmount || 0)}</span></div>
    </div>
    ${(t.deductions || []).length > 0 ? `<div class="section"><h3>Deductions</h3>${t.deductions.map((d: any) => `<div class="row"><span style="color:#dc2626">${d.deductionType}: ${d.reason}</span><span style="color:#dc2626;font-weight:600">-\u20B9${fmt(d.amount)}</span></div>`).join('')}</div>` : ''}
    ${t.transactionType !== 'BUYBACK' ? `<div class="section"><h3>Exchange</h3><div class="row"><span>New Jewellery Cost</span><span>\u20B9${fmt(t.exchangeSubtotal || 0)}</span></div><div class="row"><span>Old Gold Credit</span><span style="color:#16a34a">-\u20B9${fmt(t.finalAmount || 0)}</span></div><div class="row total"><span>${(t.exchangeBalance || 0) >= 0 ? 'Balance Payable' : 'Refund Due'}</span><span>\u20B9${fmt(Math.abs(t.exchangeBalance || 0))}</span></div></div>` : ''}
    <div class="section"><h3>Payment (Manual Record)</h3><div class="row"><span>Amount Paid</span><span style="color:#16a34a">\u20B9${fmt(t.amountPaid || 0)}</span></div><div class="row"><span>Balance To Pay</span><span style="color:#dc2626">\u20B9${fmt(t.balanceAmount ?? t.finalAmount ?? 0)}</span></div>${t.paymentNotes ? `<div class="row"><span>Note</span><span>${t.paymentNotes}</span></div>` : ''}</div>
    <div class="footer">Generated by ApniCRM · ${new Date().toLocaleString('en-IN')}</div>
    </body></html>`);
    w.document.close();
    w.focus();
    // CSP (script-src 'self') blocks inline <script> in the opened window, so the
    // print dialog is triggered from the opener instead.
    const doPrint = () => { try { w.focus(); w.print(); } catch { /* window closed */ } };
    setTimeout(doPrint, 400);
  };

  if (loading) return <GlassPanel className='p-12 text-center'><p className='text-slate-500'>Loading...</p></GlassPanel>;
  if (!txn) return <GlassPanel className='p-12 text-center'><p className='text-slate-500'>Transaction not found</p><GlassButton variant='secondary' onClick={onBack} className='mt-4'>Back</GlassButton></GlassPanel>;

  const statusIdx = STATUS_FLOW.indexOf(txn.status);

  return (
    <div>
      <div className='flex items-center gap-2 mb-4'>
        <GlassButton variant='secondary' onClick={onBack}><FaArrowLeft className='inline mr-1' /> Back to List</GlassButton>
        {onEdit && ['DRAFT', 'TESTING', 'VALUATION', 'PENDING_APPROVAL', 'APPROVED'].includes(txn.status) && (
          <GlassButton variant='secondary' onClick={() => onEdit(id)}><FaEdit className='inline mr-1' /> Edit</GlassButton>
        )}
      </div>

      <GlassPanel className='p-6 md:p-8 mb-4'>
        <div className='flex items-center justify-between flex-wrap gap-3 mb-4'>
          <div>
            <div className='flex items-center gap-2 flex-wrap'>
              <span className='font-mono text-sm text-slate-500 bg-slate-100 px-2 py-1 rounded'>{txn.transactionNumber}</span>
              <StatusPill status={txn.status} />
              <span className='text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium'>{txn.transactionType}</span>
            </div>
            <p className='text-lg font-bold text-slate-900 mt-2'>{txn.customer?.customerName || 'Unknown'}</p>
            <p className='text-sm text-slate-500'>{txn.customer?.cellNumber} · {txn.createdAt ? new Date(txn.createdAt).toLocaleString('en-IN') : ''}</p>
          </div>
          <div className='text-right'>
            <p className='text-3xl font-bold text-amber-700 flex items-center gap-1 justify-end'><FaRupeeSign />{fmt(txn.finalAmount || 0)}</p>
            <p className='text-sm text-slate-500'>Fine Gold: {fmtW(txn.totalFineGold || 0)}g</p>
          </div>
        </div>

        {/* Progress */}
        <div className='flex items-center gap-1 mb-6 overflow-x-auto'>
          {STATUS_FLOW.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                i <= statusIdx ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'
              }`}>{i + 1}</div>
              {i < STATUS_FLOW.length - 1 && <div className={`flex-1 h-0.5 min-w-[20px] ${i < statusIdx ? 'bg-green-500' : 'bg-slate-200'}`} />}
            </React.Fragment>
          ))}
        </div>
      </GlassPanel>

      {/* Items */}
      <GlassPanel className='p-6 mb-4'>
        <h3 className='font-bold text-slate-900 mb-3 flex items-center gap-2'><FaGem className='text-amber-500' /> Items ({txn.items?.length || 0})</h3>
        <div className='space-y-2'>
          {(txn.items || []).map((item: any, i: number) => (
            <div key={item.id || i} className='bg-slate-50 rounded-lg p-3 border border-slate-200'>
              <div className='flex items-center justify-between'>
                <div>
                  <span className='font-semibold text-slate-900'>{item.itemName}</span>
                  <span className='text-xs text-slate-500 ml-2 bg-slate-200 px-2 py-0.5 rounded'>{item.itemType}</span>
                  {item.hallmark && <span className='text-xs text-amber-600 ml-2'>✓ Hallmark {item.hallmarkNumber}</span>}
                </div>
                <span className='text-sm font-medium text-slate-700'>{item.quantity}x</span>
              </div>
              <div className='flex gap-4 text-xs text-slate-500 mt-1'>
                <span>Gross: <strong>{fmtW(item.grossWeight)}g</strong></span>
                <span>Stone: <strong>{fmtW(item.stoneWeight)}g</strong></span>
                <span>Net: <strong className='text-green-700'>{fmtW(item.netWeight)}g</strong></span>
              </div>
            </div>
          ))}
        </div>
      </GlassPanel>

      {/* Tests */}
      {txn.tests?.length > 0 && (
        <GlassPanel className='p-6 mb-4'>
          <h3 className='font-bold text-slate-900 mb-3 flex items-center gap-2'><FaFlask className='text-purple-500' /> Test Results</h3>
          <div className='space-y-2'>
            {txn.tests.map((t: any, i: number) => (
              <div key={t.id || i} className='bg-purple-50 rounded-lg p-3 border border-purple-200'>
                <div className='flex items-center justify-between'>
                  <span className='font-semibold text-purple-900'>{t.testingMethod}</span>
                  <span className='font-bold text-purple-700'>{t.resultPurity}% ({t.karatValue || 'N/A'})</span>
                </div>
                <p className='text-xs text-purple-600 mt-1'>Tested by: {t.testedBy} {t.machineNumber ? `· Machine: ${t.machineNumber}` : ''}</p>
              </div>
            ))}
          </div>
        </GlassPanel>
      )}

      {/* Valuation */}
      <GlassPanel className='p-6 mb-4'>
        <h3 className='font-bold text-slate-900 mb-3 flex items-center gap-2'><FaCalculator className='text-blue-500' /> Valuation</h3>
        <div className='space-y-1 text-sm'>
          <div className='flex justify-between'><span className='text-slate-600'>Total Gross Weight</span><span className='font-semibold'>{fmtW(txn.totalGrossWeight || 0)}g</span></div>
          <div className='flex justify-between'><span className='text-slate-600'>Total Stone Weight</span><span className='font-semibold'>-{fmtW(txn.totalStoneWeight || 0)}g</span></div>
          <div className='flex justify-between'><span className='text-slate-600'>Total Net Weight</span><span className='font-semibold text-green-700'>{fmtW(txn.totalNetWeight || 0)}g</span></div>
          <div className='flex justify-between'><span className='text-slate-600'>Fine Gold</span><span className='font-semibold text-amber-700'>{fmtW(txn.totalFineGold || 0)}g</span></div>
          <div className='flex justify-between'><span className='text-slate-600'>24K Rate</span><span className='font-semibold'>₹{fmt(txn.goldRate24K || 0)}/g</span></div>
          <div className='flex justify-between'><span className='text-slate-600'>24K Market Rate</span><span className='font-semibold'>₹{fmt(txn.buybackRate || 0)}/g</span></div>
          <div className='flex justify-between'><span className='text-slate-600'>Gold Value</span><span className='font-semibold'>₹{fmt(txn.totalGoldValue || 0)}</span></div>
          {(() => { const sc = Math.round((Number(txn.totalGoldValue || 0) - Number(txn.totalDeductions || 0) - Number(txn.finalAmount || 0)) * 100) / 100; return sc > 0.005 ? (<div className='flex justify-between'><span className='text-slate-600'>Store / Buyback Charges</span><span className='font-semibold text-red-600'>-₹{fmt(sc)}</span></div>) : null; })()}
          <div className='flex justify-between'><span className='text-slate-600'>Deductions</span><span className='font-semibold text-red-600'>-₹{fmt(txn.totalDeductions || 0)}</span></div>
          <div className='flex justify-between border-t pt-2 text-lg'><span className='font-bold'>Final Amount</span><span className='font-bold text-green-700'>₹{fmt(txn.finalAmount || 0)}</span></div>
        </div>
      </GlassPanel>

      {/* Deductions */}
      {txn.deductions?.length > 0 && (
        <GlassPanel className='p-6 mb-4'>
          <h3 className='font-bold text-slate-900 mb-3'>Deductions</h3>
          {txn.deductions.map((d: any, i: number) => (
            <div key={d.id || i} className='flex justify-between text-sm py-1 border-b border-slate-100 last:border-0'>
              <span className='text-red-600'>{d.deductionType}: {d.reason}</span>
              <span className='font-semibold text-red-700'>-₹{fmt(d.amount)}</span>
            </div>
          ))}
        </GlassPanel>
      )}

      {/* Exchange Info */}
      {txn.transactionType !== 'BUYBACK' && (
        <GlassPanel className='p-6 mb-4'>
          <h3 className='font-bold text-slate-900 mb-3 flex items-center gap-2'><FaExchangeAlt className='text-blue-500' /> Exchange Details</h3>
          <div className='space-y-1 text-sm'>
            <div className='flex justify-between'><span className='text-slate-600'>Exchange Subtotal</span><span className='font-semibold'>₹{fmt(txn.exchangeSubtotal || 0)}</span></div>
            <div className='flex justify-between'><span className='text-slate-600'>Old Gold Credit</span><span className='font-semibold text-green-700'>-₹{fmt(txn.finalAmount || 0)}</span></div>
            <div className='flex justify-between border-t pt-1'>
              <span className='font-bold'>{(txn.exchangeBalance || 0) >= 0 ? 'Balance Payable' : 'Refund Due'}</span>
              <span className='font-bold'>₹{fmt(Math.abs(txn.exchangeBalance || 0))}</span>
            </div>
          </div>
        </GlassPanel>
      )}

      {/* Payment — manual entries only */}
      <GlassPanel className='p-6 mb-4'>
        <h3 className='font-bold text-slate-900 mb-3 flex items-center gap-2'><FaRupeeSign className='text-green-500' /> Payment (manual)</h3>
        <p className='text-xs text-slate-500 mb-4'>
          Type by hand how much the customer has been paid and the balance still to pay. These are
          records only — the CRM does not calculate them.
        </p>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <div>
            <label className='block text-slate-600 text-xs font-semibold mb-1'>Amount Paid (₹)</label>
            <input
              type='number'
              min='0'
              value={payPaid}
              onChange={e => setPayPaid(e.target.value)}
              className='w-full px-4 py-2 border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:border-slate-400'
            />
          </div>
          <div>
            <label className='block text-slate-600 text-xs font-semibold mb-1'>Balance To Pay (₹)</label>
            <input
              type='number'
              min='0'
              value={payBalance}
              onChange={e => setPayBalance(e.target.value)}
              className='w-full px-4 py-2 border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:border-slate-400'
            />
            <button
              type='button'
              onClick={() => setPayBalance(String(Math.max((Number(txn.finalAmount) || 0) - (Number(payPaid) || 0), 0)))}
              className='text-xs text-blue-600 hover:underline mt-1'
            >
              Auto: Final Amount − Amount Paid
            </button>
          </div>
        </div>
        <div className='flex items-center justify-between flex-wrap gap-3 mt-4'>
          <div className='text-sm text-slate-600'>
            Final Amount: <strong className='text-amber-700'>₹{fmt(txn.finalAmount || 0)}</strong>
          </div>
          <GlassButton variant='primary' onClick={handleSavePayment} disabled={paySaving}>
            {paySaving ? 'Saving...' : 'Save Payment'}
          </GlassButton>
        </div>
      </GlassPanel>

      {/* Approval */}
      <GlassPanel className='p-6 mb-4'>
        <h3 className='font-bold text-slate-900 mb-3 flex items-center gap-2'><FaClipboardCheck className='text-green-500' /> Approval</h3>
        <div className='space-y-2 text-sm'>
          <div className='flex justify-between'>
            <span className='text-slate-600'>Manager Approval</span>
            <span className={txn.managerApproved ? 'text-green-600 font-semibold' : 'text-amber-600'}>{txn.managerApproved ? `✓ Approved by ${txn.managerName || 'Manager'}` : 'Pending'}</span>
          </div>
          <div className='flex justify-between'>
            <span className='text-slate-600'>Customer Approval</span>
            <span className={txn.customerApproved ? 'text-green-600 font-semibold' : 'text-amber-600'}>{txn.customerApproved ? `✓ Approved (${txn.approvalMethod || 'Signature'})` : 'Pending'}</span>
          </div>
          {txn.approvedAt && (
            <div className='flex justify-between'>
              <span className='text-slate-600'>Approved At</span>
              <span className='font-medium'>{new Date(txn.approvedAt).toLocaleString('en-IN')}</span>
            </div>
          )}
        </div>
      </GlassPanel>

      {/* Actions */}
      <GlassPanel className='p-6'>
        <h3 className='font-bold text-slate-900 mb-3'>Actions</h3>
        <div className='flex flex-wrap gap-2'>
          <GlassButton variant='secondary' onClick={() => generatePDF(txn)}>
            <FaDownload className='inline mr-1' /> Download PDF
          </GlassButton>
          {txn.status === 'DRAFT' && (
            <GlassButton variant='primary' disabled={actionLoading} onClick={() => handleAdvanceStatus('TESTING')}>
              Move to Testing
            </GlassButton>
          )}
          {txn.status === 'TESTING' && (
            <GlassButton variant='primary' disabled={actionLoading} onClick={() => handleAdvanceStatus('VALUATION')}>
              Move to Valuation
            </GlassButton>
          )}
          {txn.status === 'VALUATION' && (
            <GlassButton variant='primary' disabled={actionLoading} onClick={() => handleAdvanceStatus('PENDING_APPROVAL')}>
              Submit for Approval
            </GlassButton>
          )}
          {txn.status === 'PENDING_APPROVAL' && !txn.managerApproved && (
            <GlassButton variant='primary' disabled={actionLoading} onClick={handleManagerApprove}>
              Manager Approve
            </GlassButton>
          )}
          {/* Customer approval is valid while pending manager approval or once
              approved. Manager approval flips the status to APPROVED, so keying
              this off PENDING_APPROVAL alone made the button vanish. */}
          {!txn.customerApproved && ['PENDING_APPROVAL', 'APPROVED'].includes(txn.status) && (
            <GlassButton variant='primary' disabled={actionLoading} onClick={handleCustomerApprove}>
              Customer Approve
            </GlassButton>
          )}
          {txn.status === 'APPROVED' && txn.managerApproved && txn.customerApproved && (
            <GlassButton variant='primary' disabled={actionLoading} onClick={() => handleAdvanceStatus('COMPLETED')}>
              Complete Transaction
            </GlassButton>
          )}
          {!['COMPLETED', 'CANCELLED'].includes(txn.status) && (
            <GlassButton variant='secondary' disabled={actionLoading} onClick={handleCancel} className='text-red-600'>
              Cancel
            </GlassButton>
          )}
        </div>
      </GlassPanel>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// REPORTS VIEW
// ═══════════════════════════════════════════════════════════

const ReportsView: React.FC = () => {
  const [reportType, setReportType] = useState('daily');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await ogService.getStats();
        setStats(res.data);
      } catch (e) { console.error(e); }
    };
    loadStats();
  }, []);

  const loadReport = async () => {
    setLoading(true);
    try {
      const res = await ogService.getReports({ type: reportType, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined });
      setReportData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <div>
      {/* Stats Cards */}
      {stats && (
        <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-6'>
          <GlassCard className='text-center'><p className='text-xs text-slate-500'>Total Transactions</p><p className='text-2xl font-bold text-slate-900'>{stats.totalTransactions || 0}</p></GlassCard>
          <GlassCard className='text-center'><p className='text-xs text-slate-500'>Total Value</p><p className='text-2xl font-bold text-amber-700'>₹{fmt(stats.totalValue || 0)}</p></GlassCard>
          <GlassCard className='text-center'><p className='text-xs text-slate-500'>Fine Gold</p><p className='text-2xl font-bold text-green-700'>{fmtW(stats.totalFineGold || 0)}g</p></GlassCard>
          <GlassCard className='text-center'><p className='text-xs text-slate-500'>Today's Transactions</p><p className='text-2xl font-bold text-blue-700'>{stats.today?.count || 0}</p></GlassCard>
        </div>
      )}

      <GlassPanel className='p-6 mb-4'>
        <h3 className='font-bold text-slate-900 mb-4 flex items-center gap-2'><FaChartBar /> Reports</h3>
        <div className='flex flex-wrap gap-3 items-end mb-4'>
          <div>
            <label className='block text-slate-600 text-xs font-semibold mb-1'>Report Type</label>
            <GlassSelect value={reportType} onChange={e => setReportType(e.target.value)} className='w-auto min-w-[150px]'>
              <option value='daily'>Daily Summary</option>
              <option value='buyback'>Buyback Report</option>
              <option value='exchange'>Exchange Report</option>
              <option value='customer'>Customer History</option>
              <option value='pending'>Pending Approvals</option>
            </GlassSelect>
          </div>
          <div>
            <label className='block text-slate-600 text-xs font-semibold mb-1'>From</label>
            <GlassInput type='date' value={dateFrom} onChange={e => setDateFrom(e.target.value)} className='w-auto' />
          </div>
          <div>
            <label className='block text-slate-600 text-xs font-semibold mb-1'>To</label>
            <GlassInput type='date' value={dateTo} onChange={e => setDateTo(e.target.value)} className='w-auto' />
          </div>
          <GlassButton variant='primary' onClick={loadReport} disabled={loading}>
            {loading ? 'Loading...' : <><FaFilter className='inline mr-1' /> Generate</>}
          </GlassButton>
        </div>
      </GlassPanel>

      {reportData && (
        <GlassPanel className='p-6'>
          <h4 className='font-bold text-slate-900 mb-3'>Report Results</h4>
          {reportData.customers && reportData.customers.length > 0 ? (
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-slate-200'>
                    <th className='text-left py-2 text-slate-600 font-semibold'>Customer</th>
                    <th className='text-right py-2 text-slate-600 font-semibold'>Transactions</th>
                    <th className='text-right py-2 text-slate-600 font-semibold'>Fine Gold</th>
                    <th className='text-right py-2 text-slate-600 font-semibold'>Total Spent</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.customers.map((c: any, i: number) => (
                    <tr key={i} className='border-b border-slate-100'>
                      <td className='py-2'>{c.customer?.customerName || 'N/A'}{c.customer?.cellNumber ? ` · ${c.customer.cellNumber}` : ''}</td>
                      <td className='py-2 text-right'>{c.transactionCount || 0}</td>
                      <td className='py-2 text-right font-medium'>{fmtW(c.totalFineGold || 0)}g</td>
                      <td className='py-2 text-right font-bold text-amber-700'>₹{fmt(c.totalSpent || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : reportData.transactions && reportData.transactions.length > 0 ? (
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-slate-200'>
                    <th className='text-left py-2 text-slate-600 font-semibold'>Transaction #</th>
                    <th className='text-left py-2 text-slate-600 font-semibold'>Customer</th>
                    <th className='text-left py-2 text-slate-600 font-semibold'>Type</th>
                    <th className='text-left py-2 text-slate-600 font-semibold'>Status</th>
                    <th className='text-right py-2 text-slate-600 font-semibold'>Fine Gold</th>
                    <th className='text-right py-2 text-slate-600 font-semibold'>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.transactions.map((t: any) => (
                    <tr key={t.id} className='border-b border-slate-100'>
                      <td className='py-2 font-mono text-xs'>{t.transactionNumber}</td>
                      <td className='py-2'>{t.customer?.customerName || 'N/A'}</td>
                      <td className='py-2'><span className='text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded'>{t.transactionType}</span></td>
                      <td className='py-2'><StatusPill status={t.status} /></td>
                      <td className='py-2 text-right font-medium'>{fmtW(t.totalFineGold || 0)}g</td>
                      <td className='py-2 text-right font-bold text-amber-700'>₹{fmt(t.finalAmount || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className='text-slate-500 text-center py-4'>No data for the selected criteria</p>
          )}
          {reportData.summary && (
            <div className='mt-4 grid grid-cols-2 md:grid-cols-4 gap-3'>
              <GlassCard className='text-center bg-slate-50'><p className='text-xs text-slate-500'>Total</p><p className='font-bold'>{reportData.summary.totalTransactions || 0}</p></GlassCard>
              <GlassCard className='text-center bg-slate-50'><p className='text-xs text-slate-500'>Value</p><p className='font-bold'>₹{fmt(reportData.summary.totalValue || 0)}</p></GlassCard>
              <GlassCard className='text-center bg-slate-50'><p className='text-xs text-slate-500'>Fine Gold</p><p className='font-bold'>{fmtW(reportData.summary.totalFineGold || 0)}g</p></GlassCard>
              <GlassCard className='text-center bg-slate-50'><p className='text-xs text-slate-500'>Gross Weight</p><p className='font-bold'>{fmtW(reportData.summary.totalGrossWeight || 0)}g</p></GlassCard>
            </div>
          )}
        </GlassPanel>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// SETTINGS VIEW
// ═══════════════════════════════════════════════════════════

const SettingsView: React.FC = () => {
  const [rate24K, setRate24K] = useState('');
  const [rate22K, setRate22K] = useState('');
  const [buybackDiscount, setBuybackDiscount] = useState('200');
  const [currentRate, setCurrentRate] = useState<any>(null);
  const [settings, setSettings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const rateRes = await ogService.getGoldRate();
        const rate = rateRes.data.rate || rateRes.data;
        setCurrentRate(rate);
        setRate24K(String(rate.rate24K || ''));
        setRate22K(String(rate.rate22K || ''));
        setBuybackDiscount(String(rate.buybackDiscount || '200'));
      } catch (e) { console.error(e); }
      try {
        const settRes = await ogService.getSettings();
        setSettings(settRes.data.settings || []);
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  const handleRate24KChange = (v: string) => {
    setRate24K(v);
    const n = parseFloat(v) || 0;
    if (n > 0) setRate22K(String(Math.round(n * 0.9167)));
  };

  const saveRate = async () => {
    setLoading(true);
    setSaveMsg('');
    try {
      await ogService.setGoldRate({
        rate24K: parseFloat(rate24K) || 0,
        rate22K: parseFloat(rate22K) || parseFloat(rate24K) * 0.9167 || 0,
        buybackDiscount: parseFloat(buybackDiscount) || 200,
      });
      setSaveMsg('Gold rate updated successfully!');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e: any) { alert(e.response?.data?.error || 'Failed to save'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <GlassPanel className='p-6 mb-4'>
        <h3 className='font-bold text-slate-900 mb-4 flex items-center gap-2'><FaCog /> Gold Rate Management</h3>
        {currentRate && (
          <GlassCard className='mb-4 bg-amber-50 border-amber-200'>
            <p className='text-sm text-amber-800'>Current Active Rate: 24 Carat = <strong>₹{fmt(currentRate.rate24K || 0)}</strong> · Store Charges = <strong>₹{fmt(currentRate.buybackDiscount || 0)}</strong></p>
          </GlassCard>
        )}
        <GlassCard className='mb-4 bg-slate-50 border-slate-200'>
          <p className='text-sm text-slate-700 leading-relaxed'>
            <strong className='text-amber-700'>How to update:</strong> Enter today's <strong>24 Carat</strong> gold market rate
            (standard pure-gold price per gram). The 22K rate is derived automatically. Store charges are the amount the store
            deducts from the gold value. Tap <strong>Update Gold Rate</strong> to save for all future transactions.
          </p>
        </GlassCard>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-4'>
          <div>
            <label className='block text-slate-700 font-semibold mb-2'>24 Carat Market Rate (₹/gram)</label>
            <GlassInput type='number' step='0.01' value={rate24K} onChange={e => handleRate24KChange(e.target.value)} placeholder='e.g., 9150' />
          </div>
          <div>
            <label className='block text-slate-700 font-semibold mb-2'>22K Rate (₹/gram) — auto</label>
            <GlassInput type='number' step='0.01' value={rate22K} disabled placeholder='Auto from 24K' />
          </div>
          <div>
            <label className='block text-slate-700 font-semibold mb-2'>Store Charges (₹)</label>
            <GlassInput type='number' step='0.01' value={buybackDiscount} onChange={e => setBuybackDiscount(e.target.value)} placeholder='e.g., 200' />
          </div>
        </div>
        <p className='text-xs text-slate-500 mb-4'>Fine gold is valued at the 24 Carat market rate. Store charges are deducted from the gold value. Set a new rate to update all future transactions.</p>
        <GlassButton variant='primary' onClick={saveRate} disabled={loading}>{loading ? 'Saving...' : 'Update Gold Rate'}</GlassButton>
        {saveMsg && <p className='text-green-600 text-sm mt-2 font-medium'>{saveMsg}</p>}
      </GlassPanel>

      <GlassPanel className='p-6'>
        <h3 className='font-bold text-slate-900 mb-4 flex items-center gap-2'><FaCog /> Deduction Rules</h3>
        {settings.length > 0 ? (
          <div className='space-y-2'>
            {settings.map((s: any) => (
              <div key={s.key} className='flex items-center justify-between bg-slate-50 rounded-lg p-3 border border-slate-200'>
                <div>
                  <p className='font-medium text-slate-900'>{s.label || s.key}</p>
                  <p className='text-xs text-slate-500'>{s.category}</p>
                </div>
                <span className='font-semibold text-slate-700'>{s.value}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className='text-slate-500 text-center py-4'>No custom settings configured yet. Default deduction rules apply.</p>
        )}
      </GlassPanel>
    </div>
  );
};

export default OldGoldPage;
